import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Larry from './Larry';
import ApplyToSpeakModal from './ApplyToSpeakModal';
import { getSpeeches, getMySlots, toggleSpeechVisibility } from '../services/api';
import { showToast } from '../hooks/useToast';
import usePageTitle from '../hooks/usePageTitle';
import { FEATURES } from '../config/features';

const LIMIT = 20;

export default function MySpeeches() {
  usePageTitle('My Speeches');
  const [speeches, setSpeeches] = useState([]);
  const [upcomingSlots, setUpcomingSlots] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [speechesRes, slotsRes] = await Promise.all([
        getSpeeches('my', LIMIT, 0),
        FEATURES.SCHEDULING ? getMySlots() : Promise.resolve(null),
      ]);
      if (speechesRes?.speeches) {
        setSpeeches(speechesRes.speeches);
        setHasMore(speechesRes.speeches.length === LIMIT);
      }
      if (slotsRes?.slots) {
        const upcoming = slotsRes.slots.filter(s =>
          s.status === 'booked' && s.meeting?.meeting_date >= new Date().toISOString().split('T')[0]
        );
        setUpcomingSlots(upcoming);
      }
    } catch (err) {
      console.error('[MySpeeches] Load error:', err);
      setError(err.message || 'Failed to load speeches');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const res = await getSpeeches('my', LIMIT, speeches.length);
      if (res?.speeches) {
        setSpeeches(prev => [...prev, ...res.speeches]);
        setHasMore(res.speeches.length === LIMIT);
      }
    } catch (err) {
      setError(err.message || 'Failed to load more speeches');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleVisibilityToggle = async (speechId, currentVisibility) => {
    setError(null);
    try {
      const nextPublic = !currentVisibility;
      await toggleSpeechVisibility(speechId, nextPublic);
      const res = await getSpeeches('my', speeches.length, 0);
      if (res?.speeches) setSpeeches(res.speeches);
      showToast(nextPublic ? 'Speech is now public' : 'Speech set to private');
    } catch (err) {
      setError(err.message || 'Failed to update visibility');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center py-16 gap-3">
        <Larry mood="thinking" size={64} message="Loading your speeches..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <Larry mood="sad" size={64} message="Something went wrong!" />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <button onClick={loadData} className="btn-bouncy text-sm px-5 py-2">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Upcoming Slots */}
      {FEATURES.SCHEDULING && (
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div
            className="w-0.5 h-3 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
          />
          <h3 className="font-mono text-xs font-medium uppercase tracking-[0.2em]"
              style={{ color: 'var(--text-muted)' }}>
            Upcoming Slots
          </h3>
        </div>
        {upcomingSlots.length === 0 ? (
          <div
            className="rounded-2xl px-6 py-5 text-center"
            style={{ border: '1px dashed var(--color-info-border)', background: 'var(--color-info-dim)' }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              No upcoming speaking slots.
            </p>
          </div>
        ) : (
          <div
            className="rounded-2xl p-4 space-y-3"
            style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}
          >
            {upcomingSlots.map((slot) => (
              <div
                key={slot.slot_id}
                className="flex items-center justify-between rounded-xl px-4 py-3"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
              >
                <div>
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>
                    {slot.meeting?.title || 'Weekly Meeting'}
                  </p>
                  <p className="text-sm mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>
                    {new Date(slot.meeting?.meeting_date).toLocaleDateString('en-IN', {
                      weekday: 'long', day: 'numeric', month: 'long',
                    })} · Slot #{slot.slot_number}
                  </p>
                </div>
                <Link
                  to={`/meetings/${slot.meeting_id}`}
                  className="text-sm font-bold transition-colors"
                  style={{ color: 'var(--accent)' }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  View →
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {showApplyModal && (
        <ApplyToSpeakModal
          bookedMeetingIds={upcomingSlots.map(s => s.meeting_id)}
          onClose={() => setShowApplyModal(false)}
          onBooked={() => { setShowApplyModal(false); fetchedRef.current = false; loadData(); }}
        />
      )}

      {/* Past Speeches */}
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div
            className="w-0.5 h-3 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
          />
          <h3 className="font-mono text-xs font-medium uppercase tracking-[0.2em]"
              style={{ color: 'var(--text-muted)' }}>
            Your Speeches
          </h3>
        </div>

        {speeches.length === 0 ? (
          <div className="card-paper p-10 text-center space-y-4">
            <div className="text-4xl">🎤</div>
            <h3 className="font-display text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Your speeches will appear here
            </h3>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {FEATURES.SCHEDULING && FEATURES.COACHING
                ? 'Claim a slot at an upcoming club meeting, or upload a recording you already have.'
                : FEATURES.SCHEDULING
                  ? 'Claim a slot at an upcoming club meeting.'
                  : FEATURES.COACHING
                    ? 'Upload a recording you already have to get coaching on it.'
                    : 'Speeches attributed to you will appear here.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {FEATURES.SCHEDULING && (
                <button onClick={() => setShowApplyModal(true)} className="btn-bouncy text-sm px-6 py-2.5">
                  Apply to Speak
                </button>
              )}
              {/* Sole destination is /analyze, which HIDDEN_ROUTEs to home when
                  COACHING is off — so the CTA goes with the tab. */}
              {FEATURES.COACHING && (
                <Link
                  to="/analyze"
                  className="text-sm font-semibold px-5 py-2.5 rounded-pill transition-colors"
                  style={{ border: '1px solid var(--border-accent)', color: 'var(--accent)', background: 'var(--accent-dim)' }}
                >
                  Upload a recording →
                </Link>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {speeches.map((speech) => (
                <SpeechDetailRow
                  key={speech.speech_id}
                  speech={speech}
                  onToggleVisibility={() => handleVisibilityToggle(speech.speech_id, speech.is_public)}
                />
              ))}
            </div>
            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-bouncy text-sm px-6 py-2 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function SpeechDetailRow({ speech, onToggleVisibility }) {
  const { speech_id, title, meeting_title, created_at, metrics, is_public, youtube_url } = speech;
  const [pendingPublic, setPendingPublic] = useState(false);

  const getThumbnail = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|[?&]v=|\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
  };

  const thumbnail = getThumbnail(youtube_url);
  const wpm = metrics?.wpm;
  const confidence = metrics?.confidence_score;

  return (
    <div className="card-paper p-4 flex items-start gap-4">
      {/* Thumbnail */}
      <div
        className="rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center w-20 sm:w-28"
        style={{ background: 'var(--bg-input)', height: '72px' }}
      >
        {thumbnail ? (
          <img src={thumbnail} alt={title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-2xl">🎤</span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link
              to={`/speeches/${speech_id}`}
              className="font-bold transition-colors line-clamp-1 block"
              style={{ color: 'var(--text-primary)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
            >
              {title}
            </Link>
            <p className="text-sm mt-0.5 font-medium truncate" style={{ color: 'var(--text-muted)' }}>{meeting_title}</p>
          </div>
          <button
            onClick={() => {
              if (!is_public) {
                setPendingPublic(true);
              } else {
                onToggleVisibility();
              }
            }}
            className="text-xs px-2 py-1 rounded-lg font-bold shrink-0 transition-colors"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
          >
            {is_public ? '👁️ Public' : '🔒 Private'}
          </button>
        </div>

        {pendingPublic && (
          <div
            className="mt-2 px-3 py-2 rounded-xl text-xs font-medium flex items-center gap-3 flex-wrap"
            style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-accent)', color: 'var(--text-secondary)' }}
          >
            <span>This will make your speech visible to all club members.</span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setPendingPublic(false)}
                className="text-xs font-bold transition-colors"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setPendingPublic(false); onToggleVisibility(); }}
                className="text-xs font-bold"
                style={{ color: 'var(--accent)' }}
              >
                Make Public
              </button>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="flex items-center gap-4 mt-2">
          {wpm !== undefined && (
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--text-muted)' }}>WPM </span>{Math.round(wpm)}
            </span>
          )}
          {confidence !== undefined && (
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Fluency </span>{Math.round(confidence)}%
            </span>
          )}
          <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {new Date(Number(created_at)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-2">
          <Link
            to={`/speeches/${speech_id}`}
            className="text-sm font-bold transition-colors"
            style={{ color: 'var(--accent)' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            View Details
          </Link>
          <Link
            to={`/speeches/${speech_id}#feedback`}
            className="text-sm font-medium transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            Feedback
          </Link>
        </div>
      </div>
    </div>
  );
}
